<template>
	<DataTable
		v-if="showTable"
		:value="editingRows"
		:loading="loading"
		dataKey="pos"
		:paginator="false"
		class="p-datatable-sm"
		:class="isColumnWidthChanged ? 'column-width-changed' : ''"
		@row-reorder="onRowReorder"
		showGridlines
		:scrollable="true"
		scrollHeight="flex"
		editMode="row"
		v-model:editingRows="editingRows"
		v-model:selection="selectedRows"
		selectionMode="multiple"
		@rowSelect="onRowSelect"
		@rowUnselect="onRowUnselect"
		@rowSelectAll="onRowSelectAll"
		@rowUnselectAll="onRowUnselectAll"
		:metaKeySelection="false"
		:resizableColumns="true"
		:autoLayout="false"
		columnResizeMode="fit"
		responsiveLayout="scroll"
		@column-resize-end="columnResize"
		:virtualScrollerOptions="validationTableVirtualScrollerOptions"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Validation.General.Position') }) }}
		</template>

		<Column v-if="!isReadOnlyMode"
				:rowReorder="true"
				headerClass="header-center not-resizable-column"
				style="text-align: center;"
				:style="[
					!isColumnWidthChanged ? 'min-width: 4rem;' : 'width: 4rem; ',
					customTableActions.length > 2 ? (customTableActions.length > 3 ? 'width: 7rem;' : 'width: 5.5rem;') : 'width: 4rem;',
					store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 'height: 2.438rem;' : ''
				]"
				:notResizableColumn="true"
		>
			<template #header v-if="customTableActions.length > 0">
				<div :style="customTableActions.length >= 5 ? 'overflow-x: scroll;' : ''">
					<template v-for="(action, index) in customTableActions" :key="action.id">
						<Button type="button"
								v-tooltip="action.description"
								:icon="action.icon ? action.icon : ''"
								class="p-button-sm"
								:class="index >= 1 ? (customTableActions.length >= 5 ? 'p-ml-1 p-mb-1 p-mt-1' : 'p-ml-1') : (customTableActions.length >= 5 ? 'p-mb-1 p-mt-1' : '')"
								style="min-width: 1.25rem; min-height: 1rem; padding: 0.15rem; width: 1.25rem;"
								@click="executeCustomTableAction(action.id)"
						/>
					</template>
				</div>
			</template>
		</Column>

		<Column selectionMode="multiple" headerClass="header-center not-resizable-column" style="width:4rem; text-align: center;" :style="store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 'height: 2.438rem;' :  ''" :notResizableColumn="true" />

		<Column field="id" :header="$t('Squeeze.Validation.General.PositionHeadline')" headerClass="not-resizable-column" style="width:4rem;" :style="store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 'height: 2.438rem;' :  ''" :notResizableColumn="true">
			<template #editor="slotProps">
				<span class="column-id">{{slotProps.data["pos"]}}</span>
				<Button v-if="slotProps.data.model.state === 'ERROR'" style="height: 1rem;" v-tooltip="$t('Squeeze.Validation.General.ErrorCells')
				+ ' '
				+ slotProps.data.model.errorText" icon="mdi mdi-alert-outline" class="p-button p-button-rounded p-button-danger p-button-text" />
			</template>
		</Column>

		<!--{{allVisibleColumns}}-->
		<template v-for="col of allVisibleColumns" :key="col.id">
			<Column
				:field="col.name"
				:headerClass="(col.dataType || '').toLowerCase() === 'amount' ? 'table-header-amount' : ''"
				:style="[
					store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 'height: 2.438rem;' :  '',
					col.colWidth ? 'width: ' + col.colWidth + 'rem;' : col.width ? 'width: ' + col.width + 'px;' : (isColumnWidthChanged ? 'width: ' + (getTranslatedDescription(col).length + 5) + 'ch;' : 'min-width: 5rem;'),
				]"
			>
				<template #header>
					<span class="p-column-title" v-tooltip="getTranslatedDescription(col)">{{ getTranslatedDescription(col) }}</span>
				</template>

				<template #body="rows">
					{{ rows.data.cells[col.name]?.value }}
				</template>

				<template #editor="editingRows">
					<!-- FIXME: Access model.cells.value in v-model -->
					<AutoComplete
						v-if="!isReadOnlyMode && (col.dataType || '').toLowerCase() === 'text' && col.lookup && col.lookup.active === true && !col.readonly"
						type="text"
						v-model="editingRows.data.cells[col.name].value"
						:minLength="col.lookup.minInputLength"
						:suggestions="filteredValues[col.name]"
						field="label"
						valueField="value"
						class="input-table input-autocomplete"
						:class="{
							'table-cell-border-by-error': editingRows.data.cells[col.name].state === 'ERROR',
							'table-cell-border-by-force': editingRows.data.cells[col.name].state === 'FORCEAPPROVAL',
							'table-cell-border-by-ok': editingRows.data.cells[col.name].state === 'OK',
							'readonly': col.readonly || isReadOnlyMode,
						}"
						:inputClass="{
							'validation-field': store.state.userSettings.viewSettings.smallDistanceBetweenFields === true
						}"
						@keydown.enter.tab.up.down.left.right="navigateCells($event, editingRows.data, col, editingRows.data.cells[col.name])"
						@keydown="onKeydown($event, editingRows.data, col)"
						@blur="onFieldChange"
						:ref="el => { setCellReference(el, editingRows.data, col) }"
						:forceSelection="!col.lookup.allowCustomValues"
						:readonly="col.readonly || isReadOnlyMode"
						@complete="searchAutocomplete($event, col, editingRows.data)"
						@item-select="onItemSelect($event, col, editingRows.data, editingRows.data.cells[col.name])"
						@focus="onFocusField($event, editingRows.data.cells[col.name], editingRows.data, col)"
						:dropdownMode="'current'"
						@click="onClickAutocomplete($event, editingRows.data, col)"
						:auto-highlight="true"
						:dropdown="(!col.readonly && col.lookup.minInputLength === 0) && !isReadOnlyMode"
					/>
					<!--
					<InputNumber
						v-else-if="col.dataType.toLowerCase() === 'amount'"
						v-model="editingRows.data.cells[col.name].value"
						mode="decimal"
						:minFractionDigits="2"
					/>-->
					<!-- FIXME: Access model.cells.value in v-model-->
					<InputText v-else class="input-table" v-model="editingRows.data.cells[col.name].value"
						@keydown.enter.tab.up.down.left.right="navigateCells($event, editingRows.data, col, editingRows.data.cells[col.name])"
						@blur="onFieldChange"
						:class="{
							'validation-field': store.state.userSettings.viewSettings.smallDistanceBetweenFields === true,
							'table-cell-amount': ((col.dataType || '').toLowerCase() === 'amount'),
							'table-cell-border-by-error': editingRows.data.cells[col.name].state === 'ERROR',
							'table-cell-border-by-force': editingRows.data.cells[col.name].state === 'FORCEAPPROVAL',
							'table-cell-border-by-ok': editingRows.data.cells[col.name].state === 'OK',
							'readonly': col.readonly || isReadOnlyMode,
						}"
						@focus="onFocusField($event, editingRows.data.cells[col.name], editingRows.data, col)"
						:ref="el => { setCellReference(el, editingRows.data, col) }"
						:readonly="col.readonly || isReadOnlyMode"
					/>
				</template>
			</Column>
		</template>

		<Column
			style="text-align: end;"
			:style="[store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 'height: 2.438rem;' :  '', isColumnWidthChanged ? 'width: 5rem;' : 'min-width: 5rem; max-width: 5rem;']"
		>
			<template #editor="slotProps">
				<Button v-if="!hideButtons" v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text" @click="deleteRow(slotProps.data.pos)" />
			</template>
		</Column>

		<template #footer>
			<div class="speed-dials">
				<ul class="dials">
					<li :class="showButton ? 'pop-in' : 'pop-out'">
						<Button
							v-if="!hideButtons"
							v-tooltip.left="$t('Squeeze.Validation.Buttons.DeleteSelectedRows')"
							type="button"
							icon="mdi mdi-delete-sweep-outline"
							class="p-button-rounded p-button-danger p-shadow-18 speed-dials"
							@click="deleteSelectedRows()"
							:disabled="selectedRows.length < 1"
						/>
					</li>
					<li :class="showButton ? 'pop-in' : 'pop-out'">
						<Button
							v-if="!hideButtons"
							v-tooltip.left="$t('Squeeze.Validation.Buttons.ResetColumnWidths')"
							type="button"
							icon="mdi mdi-table-column-width"
							class="p-button-rounded p-shadow-18 speed-dials"
							@click="resetColumnWidths"
						/>
					</li>
					<li :class="showButton ? 'pop-in' : 'pop-out'">
						<Button
							v-if="!hideButtons"
							v-tooltip.left="$t('Squeeze.Validation.Buttons.AddRow')"
							type="button"
							icon="mdi mdi-plus"
							class="p-button-rounded p-shadow-18 speed-dials"
							@click="createNewRow(null)"
						/>
					</li>
				</ul>
				<Button type="button"
					v-tooltip.left="$t('Squeeze.Validation.Buttons.TableOptions')"
					class="p-button-rounded p-shadow-18 speed-dials"
					:icon="showButton ? 'mdi mdi-table-arrow-down' : 'mdi mdi-table-arrow-up'"
					@click="showButton = !showButton"
					:disabled="hideButtons"
					style="height: 3rem; width: 3rem;"
				/>
			</div>
		</template>
	</DataTable>
</template>

<script lang="ts">
/* eslint max-lines: off */
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import {ClientManager} from "@/singletons/ClientManager";
import {
	CustomValidationActionDto,
	DocumentField,
	DocumentTable,
	DocumentTableCell,
	DocumentTableColumn,
	DocumentTableRow,
	FieldLookupFilter,
	TableColumnWidth
} from "@dex/squeeze-client-ts";
import DataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import AutoComplete from "@/components/DexAutocomplete.vue";
import {AutoCompleteOnCompleteEvent} from "@/shims-prime-vue";
import {LookupDefinition, ValidationFieldDto} from "@dex/squeeze-client-ts";
import {useSqueezeStore} from "@/apps/squeeze/store";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ToastManager} from "@/util/ToastManager";
import {TableStatus} from "@/util/TableStatus";
import {ParsingHelper} from "@/util/ParsingHelper";
import {ValidationColumnWidthStore} from "@/util/ValidationColumnWidthStore";
import {getTranslatedDescription} from "@/util/TranslationHelper";

interface DocumentTableCellWithIndex extends DocumentTableCell {
	cellIndex: number;
}

interface ColumnWidth extends DocumentTableColumn {
	colWidth?: string;
}

interface UiTableRow {
	model: DocumentTableRow;
	cells: {
		[columnName: string]: DocumentTableCell;
	};
	pos: number;
}

interface UiTableCell {
	pos: number;
	name: string;
	element: any;
	column: DocumentTableColumn;
}

interface LookupDefinitionWithFilters extends LookupDefinition {
	lookupFieldFilters: FieldLookupFilter[];
}

interface DocumentTableColumnWithLookupFilter extends DocumentTableColumn{
	lookup?: LookupDefinitionWithFilters;
}

export default defineComponent({
	name: "ValidationTable",
	components: {
		DataTable,
		Column,
		InputText,
		AutoComplete,
	},
	props: {
		/** List of all DocumentFields of document */
		documentFields: {
			type: Array as PropType<ValidationFieldDto[]>,
			default: () => [],
		},
		currentTable: {
			type: Object as PropType<DocumentTable>,
			default: () => ({}),
		},
		/** Should the buttons of the table be hidden? */
		hideButtons: {
			type: Boolean,
			default: false,
		},
		/** Is the current mode of component readonly? */
		isReadOnlyMode: {
			type: Boolean,
			default: false,
		},
		/** Current documentId **/
		documentId: {
			type: Number,
			required: true,
		},
		/** Current document class ID **/
		documentClassId: {
			type: Number,
			required: true,
		},
		/** Reset all selected rows of current table after execution of user exits */
		resetSelectedRows: {
			type: Boolean,
			default: false,
		},
		customTableActions: {
			type: Array as PropType<CustomValidationActionDto[]>,
			default: () => [],
		},
	},
	emits: [
		'onFocusField',
		'onChange',
		"onChangeTable",
		'allTableRefCells',
		'loadingStatusChange',
		'mountedReady',
		'focusLastHeadField',
		'executeCustomTableAction',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const tableLoadingStatusText = {
			requesting: TableStatus.getValidationTableLoadingStatusText(t, "requesting"),
			preparing: TableStatus.getValidationTableLoadingStatusText(t, "preparing"),
			loadingWidths: TableStatus.getValidationTableLoadingStatusText(t, "loadingWidths"),
			ready: TableStatus.getValidationTableLoadingStatusText(t, "ready"),
		};

		/** Current Vuex-Store */
		const store = useSqueezeStore();

		/** Api for document */
		const documentService = ClientManager.getInstance().squeeze.document;

		/** Show Loading on load data */
		const loading = ref<boolean>(false);
		const lastLoadingStatus = ref<string>("");

		/** Is the table visible? */
		const showTable = ref<boolean>(true);

		const table = reactive<DocumentTable>({
			id: 0,
			documentClassId: 0,
			fieldGroupId: 0,
			locatorId: 0,
			name: "",
			description: "",
			mandatory: false,
			readonly: false,
			hidden: false,
			forceValidation: false,
			externalName: "",
		});

		/** Array with all row-values */
		const rows = ref<DocumentTableRow[]>([]);

		/** Array with Table-Fields */
		const columns = ref<DocumentTableColumn[]|null>(null);

		/** Should the row be edited? */
		const editingRows = ref<UiTableRow[]>([]);

		/** Array with the currently filtered values */
		const filteredValues = ref<any[]>([]);

		/** Object with the current default-values  */
		const defaultValues = reactive<{[key: string]: any}>({});

		/** Array with Table-Cells  */
		const tableCells =ref<UiTableCell[]>([]);

		/** Next Table-Cell by Enter or Tab */
		const nextTableCell = ref<HTMLElement|null>(null);

		/** List of readonly-columns */
		const noFocusColumns = ref<DocumentTableColumn[]>([]);
		const noFocusColumnNames = computed(() => {
			return new Set(noFocusColumns.value.map((column) => column.name ?? ""));
		});

		/** Should the buttons of the table be hidden? */
		const showButton = ref<boolean>(false);

		/** Currently selected rows */
		const selectedRows = ref<UiTableRow[]>([]);

		/** List of all table column widths */
		const allTableColumnWidths = ref<TableColumnWidth[]>([]);

		/** Is column width changed? */
		const isColumnWidthChanged = ref<boolean>(false);

		/**
		 * Cached lookup for saved table column widths by table field id.
		 * Used when building visible columns so each field can restore its persisted width without repeatedly scanning all saved widths.
		 */
		const tableColumnWidthsByFieldId = computed(() => ValidationColumnWidthStore.toWidthMap(allTableColumnWidths.value));
		/**
		 * Builds the lookup key for a concrete table cell.
		 * The table cell references are stored in one flat array, so keyboard navigation needs row and column to identify the current cell.
		 */
		const getTableCellLookupKey = (rowPos: number, columnName: string) => `${rowPos}:${columnName}`;

		/**
		 * Cached lookup from table cell key to the index in `tableCells`.
		 * Used by Tab and left/right arrow navigation so we do not repeatedly scan the full cell array.
		 */
		const tableCellIndexByKey = computed(() => {
			const nextIndexByKey = new Map<string, number>();
			tableCells.value.forEach((cell, index) => {
				nextIndexByKey.set(getTableCellLookupKey(cell.pos, cell.name), index);
			});
			return nextIndexByKey;
		});

		/**
		 * Returns the flat `tableCells` index for a row/column pair.
		 * `-1` means the cell reference is currently missing, for example because the column is hidden, readonly, unnamed, or not mounted yet.
		 */
		const getTableCellIndex = (rowData: UiTableRow, column: DocumentTableColumn) => {
			return tableCellIndexByKey.value.get(getTableCellLookupKey(rowData.pos, column.name ?? '')) ?? -1;
		};
		const validationTableVirtualScrollerOptions = computed(() => {
			// ToDo: Optimize DOM ref - Many code changes needed because index and DOM ref unstable with virtualScroller
			// https://dev.azure.com/DEXPRO/DEXPRO%20Platform/_git/DEXPRO%20Platform/pullRequest/6234?discussionId=57298#1780315416
			// Wird langfristig gelöst in https://dev.azure.com/DEXPRO/DEXPRO%20Platform/_workitems/edit/17461/

			// VirtualScrollbar only temporary activated for old table with FeatureFlag to check stability for above PR
			if(store.state.featureSet.v2ValidationTable) {
				if (editingRows.value.length < 100) {
					return null;
				}
				return {
					itemSize: store.state.userSettings.viewSettings.smallDistanceBetweenFields ? 39 : 46,
					numToleratedItems: 12,
				};
			}
			return null
		});

		/** All visible columns of a table */
		const allVisibleColumns = computed(() => {
			// Filter all visible cells from Array
			if (columns.value && columns.value.length > 0) {
				return columns.value.filter((column: DocumentTableColumn) => column.hidden === false);
			}
			return [];
		});

		/** Set table column width */
		const setTableColumnWidth = () => {
			const allVisibleTableColumns: ColumnWidth[] = [];
			if (!columns.value) {
				return;
			}

			columns.value.forEach((column: ColumnWidth) => {
				if (column.id && tableColumnWidthsByFieldId.value.has(column.id)) {
					column.colWidth = tableColumnWidthsByFieldId.value.get(column.id);
				}
				if (!column.hidden) {
					allVisibleTableColumns.push(column);
				}
			});

			// remove on the last visible column the width, when all columns have a column width
			const allColumnsHasPropertyColWidth: boolean = allVisibleTableColumns.every((tableColumn: ColumnWidth) => tableColumn.colWidth);
			if (columns.value.length !== allVisibleTableColumns.length && allColumnsHasPropertyColWidth) {
				allVisibleTableColumns.forEach((column: ColumnWidth, columnIndex: number) => {
					if (columnIndex === allVisibleTableColumns.length -1) {
						column.colWidth = '';
					}
				})
			}
		}

		const setLoadingStatus = (status: string) => {
			if (lastLoadingStatus.value === status) {
				return;
			}

			lastLoadingStatus.value = status;
			emit("loadingStatusChange", status);
		}

		/** Get all table column widths */
		const getAllTableColumnWidths = async () => {
			if (!table || !table.id) {
				return;
			}

			const cachedColumnWidths = ValidationColumnWidthStore.readCache(table.id);
			if (cachedColumnWidths) {
				allTableColumnWidths.value = cachedColumnWidths;
				if (cachedColumnWidths.length > 0) {
					await nextTick();
					setTableColumnWidth();
				}
				return;
			}

			loading.value = true;
			setLoadingStatus(tableLoadingStatusText.loadingWidths);
			try {
				const allColumnWidths = await ValidationColumnWidthStore.fetchFromApi(props.documentClassId, table.id);
				allTableColumnWidths.value = allColumnWidths;
				if (allColumnWidths.length > 0) {
					ValidationColumnWidthStore.writeCache(table.id, allColumnWidths);
					setTableColumnWidth();
				}
			} catch (response: any) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), response);
			} finally {
				loading.value = false;
				setLoadingStatus(tableLoadingStatusText.ready);
			}
		}

		/** Trigger the column resize
		 * @param event
		 */
		const columnResize = (event: any) => {
			const columnWidthInRem = ParsingHelper.pxToRem(event.element.offsetWidth);

			// get current column
			const currentColumn = columns.value!.find((column: ColumnWidth) => getTranslatedDescription(column) === event.element.innerText) as ColumnWidth;

			if (!currentColumn || !currentColumn.id || !columnWidthInRem || !table || !table.id) {
				return;
			}

			const columnId = currentColumn.id;
			const tableId = table.id;
			const widthInRem = String(columnWidthInRem);

			const widthExists = allTableColumnWidths.value.some((tableColumnWidth: TableColumnWidth) => tableColumnWidth.tableFieldId === columnId);

			ValidationColumnWidthStore.persistWidth(props.documentClassId, tableId, columnId, widthInRem, widthExists)
				.then(() => {
					currentColumn.colWidth = widthInRem;

					const currentWidth = allTableColumnWidths.value.find((tableColumnWidth: TableColumnWidth) => tableColumnWidth.tableFieldId === columnId);
					if (currentWidth) {
						currentWidth.width = widthInRem;
					} else {
						allTableColumnWidths.value.push({tableFieldId: columnId, width: widthInRem});
					}

					ValidationColumnWidthStore.writeCache(tableId, allTableColumnWidths.value);
				})
				.catch((response: any) => ToastManager.showError(toast, t('Squeeze.General.Error'), response));
		}

		/**
		 * Reloads the Table-data
		 * @param {boolean} reloadColumns
		 */
		const reloadData = (reloadColumns: boolean = true) => {
			setLoadingStatus(tableLoadingStatusText.preparing);
			editingRows.value = [];

			if (!table || !table.columns) {
				setLoadingStatus(tableLoadingStatusText.ready);
				return;
			}

			// If the columns are always reloading, there will be errors with focusing
			if (reloadColumns) {
				columns.value = table.columns;
				noFocusColumns.value = table.columns.filter(col => col.readonly || col.hidden);
			}

			const rows = table.rows;

			if (rows) {
				let cellIndex = 0;
				editingRows.value = rows.map((row: DocumentTableRow, index: number) => {
					const editingRow: UiTableRow = {
						model: Object.assign({}, row),
						pos: (index + 1),
						cells: {},
					};

					row.cells?.forEach((cell: any) => {
						const cellWithIndex = cell as DocumentTableCellWithIndex;
						if (!noFocusColumnNames.value.has(cell.columnName ?? '')) {
							cellWithIndex.cellIndex = cellIndex;
							cellIndex++;
						}
						editingRow.cells[cell.columnName!] = cellWithIndex;
					});

					return editingRow;
				});
			} else {
				// Create Reference to Rows-Object if there is no Reference before
				table.rows = [];
			}

			if (columns.value) {
				// check if one or more columns have a column width
				if (columns.value.filter(column => column.width).length > 0) {
					isColumnWidthChanged.value = true;
				} else {
					isColumnWidthChanged.value = false;
				}

				// Create default-values
				columns.value.forEach(column => {
					if (defaultValues) {
						defaultValues["id"] = 0;
						if (column.name) {
							defaultValues[column.name] = {};
							defaultValues[column.name].value = "";
						}
					}
				})
			}

			setLoadingStatus(tableLoadingStatusText.ready);
		}

		/**
		 * Event that is triggered when an item is selected on Autocomplete
		 * @param event Event of Autocomplete
		 * @param documentClassField Current documentClassField
		 * @param row Current row
		 * @param cellData Current cell data
		 */
		const onItemSelect = (event: any, documentClassField:  DocumentTableColumn, row: UiTableRow, cellData: DocumentTableCell) =>{
			// Set the field-value to "value", not the label
			if (documentClassField && documentClassField.name) {
				row.cells[documentClassField.name].value = event.value.value;

				if (cellData.state === "FORCEAPPROVAL") {
					cellData.state  = "OK";
					emit("onChange");
				}
			}
		}

		/**
		 * Event that is triggered when users make autocomplete-inputs
		 * @param event Event of Autocomplete
		 * @param field Current documentClassField
		 * @param data Current table row data
		 */
		const searchAutocomplete = async (event: AutoCompleteOnCompleteEvent, field: DocumentTableColumn, data: UiTableRow) => {
			const rows = await documentService.getDocumentTableCellLookupValues(props.documentId, table.id!, field.id!, {
				documentFields: props.documentFields,
				fieldSearchValue: event.query,
				documentTableRow: data.model,
			})
			const resultColumns = field.lookup?.resultValueColumnIds;
			const alternatives = rows
				.map(row => {
					const completeValue = row;
					const value = row.displayColumnResults![field.lookup!.resultKeyColumnId];
					const label = resultColumns?.map(col => row.displayColumnResults![col!]!).join(" | "); // Map result columns to a single string to be displayed
					return {value, label, completeValue};
				})

			filteredValues.value[field.name! as any] = alternatives;
		}

		/**
		 * Focus the active cell
		 * @param cell
		 */
		const activateCell = (cell?: UiTableCell) => {
			if (cell && cell.element && cell.column && cell.column.lookup) {
				const $el = cell.element.$el;
				const el = (!cell.column.lookup.active || props.isReadOnlyMode ? $el : $el.firstElementChild) as HTMLInputElement;
				nextTableCell.value = el;

				el.focus({ preventScroll: false });
			}
		}

		/** Set after inserting a row so it gets re-focused once the table re-renders (validation rebuild). */
		let pendingScrollToBottom = false;

		/** Scroll to table bottom */
		const scrollToTableBottom = async () => {
			await nextTick();

			// The virtual scroller (active for large tables) renders only the visible rows and scrolls on
			// its own element, so scroll the new (bottom) row into view before focusing it. Rows render
			// asynchronously after scrolling, so retry until the last row's cell exists.
			const scroller = document.querySelector('.p-virtualscroller') ?? document.querySelector('.p-datatable-wrapper');
			const lengthOfAllTableRows = table.rows ? table.rows.length : 0;

			for (let attempt = 0; attempt < 12; attempt++) {
				if (scroller) {
					scroller.scrollTo({ top: scroller.scrollHeight });
				}
				await nextTick();
				await new Promise<void>(resolve => window.setTimeout(resolve, 30));

				const firstCellOfLastRow = tableCells.value.find(cell => cell.pos === lengthOfAllTableRows);
				if (firstCellOfLastRow) {
					activateCell(firstCellOfLastRow);
					return;
				}
			}
		}

		/** Create empty row model object */
		const createEmptyRowModelObject = () => {
			/** Create default-values */
			const row = {
				cells: [] as DocumentTableCell[],
				value: {
					value: '',
					page: 0,
					text: '',
					type: '',
					confidence: 0,
				},
			}

			if (!columns.value) {
				return row;
			}

			const cells: DocumentTableCell[] = [];
			columns.value.forEach(column => {
				const cell: DocumentTableCell = {
					columnName: column.name,
					value: '',
				}

				cells.push(cell);
			})
			row.cells = cells;

			return row;
		}

		/**
		 * Creates a new row and adds it to the bottom if the table.
		 * @param currentRowIndex
		 */
		const createNewRow = async (currentRowIndex: number) => {
			if (defaultValues && editingRows.value && table.rows) {
				const model = createEmptyRowModelObject();

				if (currentRowIndex) {
					// Allow inserting at any place in the table
					table.rows.splice(currentRowIndex, 0, model);
				} else {
					table.rows.push(model);
				}

				// Map table-rows to editing-table
				tableCells.value.splice(0);
				let cellIndex = 0;
				editingRows.value = table.rows.map((row: DocumentTableRow, index: number) => {
					const editingRow: UiTableRow = {
						model: Object.assign({}, row),
						pos: (index + 1),
						cells: {},
					};

					row.cells?.forEach((cell: DocumentTableCell) => {
						const cellWithIndex = cell as DocumentTableCellWithIndex;
						if (!noFocusColumnNames.value.has(cell.columnName ?? '')) {
							cellWithIndex.cellIndex = cellIndex;
							cellIndex++;
						}
						editingRow.cells[cell.columnName!] = cellWithIndex;
					});

					return editingRow;
				});
			}

			// validate the new rows
			emit("onChange");

			if (!currentRowIndex) {
				pendingScrollToBottom = true;
				await scrollToTableBottom();
			} else {
				await nextTick();
				const firstCellOfCopiedRow = tableCells.value.find(cell => cell.pos == currentRowIndex +1);
				activateCell(firstCellOfCopiedRow);
			}
		}

		/**
		 * Creates a copy row of the currentRow and adds it to the next line if the table.
		 * @param currentRowIndex
		 */
		const copyRow = async (currentRowIndex: number) => {
			const currentRow: DocumentTableRow | undefined = table.rows?.find((row, index: number) => index === currentRowIndex - 1);

			if (currentRow && table.rows) {
				table.rows.splice(currentRowIndex, 0, currentRow);
				let cellIndex = 0;
				editingRows.value = table.rows.map((row: DocumentTableRow, index: number) => {
					const editingRow: UiTableRow = {
						model: Object.assign({}, row),
						pos: (index + 1),
						cells: {},
					};

					row.cells?.forEach((cell: any, indexCol) => {
						const cellWithIndex = cell as DocumentTableCellWithIndex;
						if (!noFocusColumnNames.value.has(cell.columnName ?? '')) {
							cellWithIndex.cellIndex = cellIndex;
							cellIndex++;
						}
						editingRow.cells[cell.columnName!] = cellWithIndex;
					});

					return editingRow;
				});
			}

			// validate the new rows
			emit("onChange");

			await nextTick();
			const firstCellOfCopiedRow = tableCells.value.find(cell => cell.pos == currentRowIndex +1);
			activateCell(firstCellOfCopiedRow);
		}

		/** Reset all column widths of table */
		const resetColumnWidths = async () => {
			if (columns.value && table && table.id) {
				const requests = columns.value.map((column: ColumnWidth) => {
					if (typeof column.colWidth !== 'undefined' && column.id) {
						return ValidationColumnWidthStore.clearWidth(props.documentClassId, table.id!, column.id)
							.catch((response: any) => ToastManager.showError(toast, t('Squeeze.General.Error'), response));
					}
					return Promise.resolve();
				});

				await Promise.all(requests)
					.then(() => {
						columns.value!.forEach((column: ColumnWidth) => {
							column.colWidth = '';
						});

						ValidationColumnWidthStore.clearCache(table.id!);
					});

				columns.value = [...columns.value];

				// force a re-render
				showTable.value = false;
				await nextTick();
				showTable.value = true;
			}
		}

		/** Deletes all selected rows in table */
		const deleteSelectedRows = () => {
			// delete all rows
			if (selectedRows.value.length === editingRows.value.length) {
				editingRows.value.splice(0);
				table.rows = [];
				selectedRows.value = [];
				emit("onChangeTable", table);
				return;
			}

			selectedRows.value.forEach(selectedRow => {
				const selectedRowIndex = editingRows.value.findIndex(editingRow => editingRow.pos === selectedRow.pos);
				if (selectedRowIndex || selectedRowIndex === 0) {
					// delete the selected row
					editingRows.value.splice(selectedRowIndex, 1);

					selectedRows.value = [];
				}
			});

			// set new row position
			editingRows.value.forEach((row, index) => {
				row.pos = (index + 1);
			});

			table.rows = editingRows.value.map(row => {
				return {
					value: row.model.value,
					cells: row.model.cells,
				}
			});

			emit("onChangeTable", table);
		}

		/**
		 * Find cell
		 * @param row
		 * @param col
		 */
		const findCell = (row: number, col?: DocumentTableColumn) => {
			if (col) {
				return tableCells.value.find(cell => cell.pos === row && cell.name === col.name);
			}
			return tableCells.value.find(cell => cell.pos === row);
		}

		/**
		 * Deletes a row the table
		 * @param {number} index
		 */
		const deleteRow = (index: number) => {
			editingRows.value.splice(index -1, 1);

			editingRows.value.forEach((row, index) => {
				row.pos = (index + 1);
			});

			table.rows = editingRows.value.map(row => {
				return {
					value: row.model.value,
					cells: row.model.cells,
				}
			});

			emit("onChangeTable", table);

			if (editingRows.value.length >= index) {
				activateCell(findCell(index));
			} else if (editingRows.value.length) {
				activateCell(findCell(editingRows.value.length));
			}
		}

		/** Creates the default values that are used for new entries */
		const createDefaultValues = () => {
			/** Create default-values */
			if (!columns.value) {
				return;
			}

			columns.value.forEach(column => {
				if (defaultValues) {
					defaultValues["id"] = 0;
					if (column.name) {
						defaultValues[column.name] = {};
						defaultValues[column.name].value = "";
					}
				}
			})
		}

		/**
		 * Triggered when a field is focused
		 * @param event
		 * @param cell
		 * @param row
		 * @param col
		 */
		const onFocusField = (event: any, cell: DocumentTableCell, row: UiTableRow, col: DocumentTableColumn) => {
			if (event && event.target) {
				// Store original value for change detection on blur
				(event.target as HTMLInputElement).dataset.originalValue = cell.value ?? '';
				// Select field
				(event.target as HTMLInputElement).select();
			}

			// Remove Reference and create Document Field from cell-data (for error-messages)
			const tableField = JSON.parse(JSON.stringify(cell)) as DocumentField;
			tableField.description = getTranslatedDescription(col)  + " (" + t("Squeeze.Validation.General.Row") + " " + row.pos + ")";
			tableField.value = {
				value: cell.value,
				state: cell.state,
				errorText: cell.errorText,
			}

			// Set message for fields, TODO: change when backend does this
			if (cell.errorCode !== -1 && cell.errorCode != null) {
				tableField.value!.errorText = t("Squeeze.Validation.ErrorCode." + cell.errorCode);
			} else if (cell.state?.toLowerCase() === "ok") {
				tableField.value!.errorText = t("Squeeze.Validation.ErrorCode.0");
			}

			emit("onFocusField", cell, row.model, row.pos, tableField);
		}

		/**
		 * Check if table row in viewport
		 * @param parent table wrapper
		 * @param row current row element
		 * @returns returns a boolean if row in parent bounding client rect
		 */
		const isTableRowInViewport = (parent: Element, row: Element) => {
			const rowRect = row.getBoundingClientRect();
			const parRect = parent.getBoundingClientRect();
			return (
				rowRect.top >= parRect.top &&
				rowRect.left >= parRect.left &&
				rowRect.bottom <= parRect.bottom &&
				rowRect.right <= parRect.right
			);
		}

		/**
		 * Calculates what table rows are visible to the user
		 * @param container table wrapper
		 * @returns Indexes of table rows visible to the user
		 */
		const determineVisibleTableRows = (container: Element) => {
			const rowElement = container.querySelectorAll('.p-selectable-row');
			const visibleRows: number[] = [];
			let i: number, j: number, currentRow: Element;

			for (i = 0, j = rowElement.length; i < j; i++) {
				currentRow = rowElement[i];

				if (isTableRowInViewport(container, currentRow)) {
					visibleRows.push(i);
				}
			}
			return visibleRows;
		}

		/**
		 * Scroll to table cell
		 * @param nextCell
		 * @param isPrevElement is next cell a previous element?
		 */
		const scrollToTableCell = (nextCell: UiTableCell, isPrevElement: boolean) => {
			// scroll to nextCellElement position when next row not completely visible
			const container = document.querySelector('.p-datatable-wrapper');
			const visibleRows: number[] = determineVisibleTableRows(container!);
			visibleRows.shift();
			if (!visibleRows.find((row: number) => row === (nextCell.pos -1))) {
				const nextCellElement = nextCell.element.$el;
				if (nextCellElement && container) {
					// check if nextCell on row previous or next element, when not then scrollIntoView
					if (!visibleRows.find((row: number) => row -1 === nextCell.pos -1 || row +1 === nextCell.pos -1)) {
						nextCellElement.scrollIntoView(false);
					} else {
						container.scrollTo({
							top: isPrevElement ? (container.scrollTop - nextCellElement.scrollHeight - 6) : (container.scrollTop + nextCellElement.scrollHeight + 6),
							left: 0,
							behavior: 'smooth',
						});
					}
				}
			}
		}

		/**
		 * Navigate in table cells
		 * @param event
		 * @param rowData
		 * @param column
		 * @param cellData
		 */
		const navigateCells = (event: KeyboardEvent, rowData: UiTableRow, column: DocumentTableColumn, cellData: DocumentTableCell) => {
			let nextCell: UiTableCell | undefined;
			const target = event.target as HTMLElement;
			const parent = target.parentElement;

			// Forced approvals are cleared as soon as the user navigates away from the cell.
			if(cellData.state === "FORCEAPPROVAL") {
				cellData.state  = "OK";
				emit("onChange");
			}

			// Enter moves vertically within the same column; Shift+Enter moves up.
			if (event.key === 'Enter') {
				if (target.tagName === 'INPUT' && parent?.tagName === 'SPAN' && parent?.getAttribute('aria-expanded') === 'true') {
					// Autocomplete offen -> Navigation im Autocomplete hat Vorrang
					return;
				}
				nextCell = findCell((event.shiftKey) ? rowData.pos - 1 : rowData.pos + 1);

				if (!nextCell) {
					emit("focusLastHeadField");
				} else {
					scrollToTableCell(nextCell, event.shiftKey);
				}
			} else if (event.key === 'Tab') {
				// Tab follows the flat editable cell order; Shift+Tab moves backwards.
				const index = getTableCellIndex(rowData, column);

				if (event.shiftKey) {
					if (index > 0) {
						nextCell = tableCells.value[index - 1];
					}
				} else {
					if (index >= 0 && index < tableCells.value.length - 1) {
						nextCell = tableCells.value[index + 1];
					}
				}

				if (!nextCell) {
					event.preventDefault();
					emit("focusLastHeadField");
				} else {
					scrollToTableCell(nextCell, event.shiftKey);
				}
			} else if (event.key === 'ArrowDown') {
				// ArrowDown keeps the current column and moves one row down.
				if (target.tagName === 'INPUT' && parent?.tagName === 'SPAN' && parent?.getAttribute('aria-expanded') === 'true') {
					// Autocomplete offen -> Navigation im Autocomplete hat Vorrang
					return;
				}
				nextCell = findCell(rowData.pos + 1, column);
			} else if (event.key === 'ArrowUp') {
				// ArrowUp keeps the current column and moves one row up.
				if (target.tagName === 'INPUT' && parent?.tagName === 'SPAN' && parent?.getAttribute('aria-expanded') === 'true') {
					// Autocomplete offen -> Navigation im Autocomplete hat Vorrang
					return;
				}
				nextCell = findCell(rowData.pos - 1, column);
				if (nextCell) {
					scrollToTableCell(nextCell, true);
				}
			} else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
				// Left/right only leave the input when the cursor is already at the text boundary.
				const input = target as HTMLInputElement;
				const len = input.value ? input.value.length : 0;

				// Keep normal cursor movement when the user has selected text.
				if (input.selectionStart !== input.selectionEnd) {
					return;
				}

				if (event.key === 'ArrowLeft' && input.selectionStart === 0 && input.selectionEnd === 0) {
					const index = getTableCellIndex(rowData, column);
					if (index > 0) {
						nextCell = tableCells.value[index - 1];
						if (nextCell) {
							scrollToTableCell(nextCell, true);
						}
					}
				} else if (event.key === 'ArrowRight' && input.selectionStart === len && input.selectionEnd === len) {
					const index = getTableCellIndex(rowData, column);
					if (index >= 0 && index < tableCells.value.length - 1) {
						nextCell = tableCells.value[index + 1];
						if (nextCell) {
							scrollToTableCell(nextCell, false);
						}
					}
				}
			} else {
				return;
			}

			// Only take over browser keyboard handling when a target cell was found.
			if (nextCell) {
				event.preventDefault();
				activateCell(nextCell);
			}
		}

		/**
		 * Triggered when field changed
		 * @param event
		 */
		const onFieldChange = (event: any) => {
			// In AutoComplete box if entry in popup list is clicked
			// first the input's blur event is triggered.
			// But in this case no further action (Validation) should be taken
			// but only after real blur (leave) of input.
			// This check MUST come first, before the blur value comparison,
			// because when selecting an item the value hasn't changed yet (onItemSelect sets it later).
			if (event && event.target) {
				const node = event.target as Node;
				if (node.parentElement && node.parentElement.getAttribute('aria-expanded') === 'true') {
					return;
				}
			}

			if (event && event.type === 'blur') {
				// Check if this is an autocomplete field (input inside p-autocomplete span)
				const isAutocomplete = event.target?.parentElement?.classList?.contains('p-autocomplete');

				// Skip validation if the field value hasn't actually changed
				// Compare current value against the original value stored on focus
				// Note: Skip this check for autocomplete fields because originalValue gets reset
				// after item selection when focus returns to the input
				if (!isAutocomplete && event.target && event.target.value === (event.target.dataset?.originalValue ?? event.currentTarget?.value)) {
					return;
				}

				// Prevent double validation when focus moves to the viewer (right-click case)
				const rt = event.relatedTarget as HTMLElement | null;
				if (rt && (rt.id === 'SqueezeViewer' || rt.closest('#SqueezeViewer'))) return;
			}

			emit("onChange");
		}

		/**
		 * Set cell reference
		 * @param element
		 * @param rowData
		 * @param column
		 */
		const setCellReference = async (element: Element, rowData: UiTableRow, column: DocumentTableColumn) => {
			if (column.readonly === true || column.hidden === true) {
				return
			}

			const cell = rowData.cells[column.name!] as DocumentTableCellWithIndex;
			tableCells.value[cell.cellIndex] = {
				pos: rowData.pos,
				name: column.name || '',
				element: element,
				column: column,
			};
		}

		/**
		 * Event that is triggered on click on autocomplete-fields
		 * @param {KeyboardEvent} event
		 * @param {UiTableRow} rowData
		 * @param {DocumentTableColumn} column
		 */
		const onClickAutocomplete = (event: KeyboardEvent, rowData: UiTableRow, column: DocumentTableColumn) => {
			// Trigger onDropDownClick-Event, so the alternatives are shown when there are alternatives. Otherwise do nothing
			if (column.lookup?.active === true && column.lookup.minInputLength === 0) {
				const cell = findCell(rowData.pos, column);

				// Open Dropdown if there is one
				if (cell && cell.element && cell.element.onDropdownClick) {
					cell.element.onDropdownClick(event, column);
				}
			}
		}

		/**
		 * Triggered when a Dropdown is clicked. Currently, this triggered by clicking the Autocomplete-Field.
		 * @param event
		 * @param field
		 */
		const onClickDropdown = (event: AutoCompleteOnCompleteEvent, field: DocumentTableColumnWithLookupFilter) => {
			event.query = "";
		}

		/**
		 * Triggered when the rows are reordered
		 * @param event
		 */
		const onRowReorder = (event: any) => {
			if (!event.value) {
				return
			}

			table.rows = event.value.map((row: UiTableRow) => {
				return {
					value: row.model.value,
					cells: row.model.cells,
				}
			});

			emit("onChangeTable", table);
		}

		/**
		 * Execute the custom action in table
		 * @param {string} actionId
		 */
		const executeCustomTableAction = (actionId: string) => {
			emit("executeCustomTableAction", actionId);
		}

		/**
		 * Triggered on keydown
		 * @param {KeyboardEvent} event
		 * @param {UiTableRow} rowData
		 * @param {DocumentTableColumn} column
		 */
		const onKeydown = (event: KeyboardEvent, rowData: UiTableRow, column: DocumentTableColumn) => {
			// if key ctrl and arrow down pressed, then open the autocomplete popup
			if (event.code === 'ArrowDown' && (navigator.platform.match("MacIntel") ? event.metaKey : event.ctrlKey)) {
				onClickAutocomplete(event, rowData, column);
			}
		}

		onMounted(() => {
			Object.assign(table, props.currentTable);
			setLoadingStatus(tableLoadingStatusText.requesting);
			getAllTableColumnWidths();
			reloadData();
			emit("allTableRefCells", tableCells.value);
			nextTick(() => {
				emit("mountedReady");
			});
		})

		/**
		 * Triggered, when the row is selected
		 * @param event
		 */
		const onRowSelect = (event: { originalEvent: Event; data: UiTableRow; index: number; type: string }) => {
			if (event.type === "checkbox") {
				if (table && table.rows) {
					table.rows[(event.data.pos -1)].selected = true;
					emit("onChangeTable", table, true);
				}
			}
		}

		/**
		 * Triggered, when the row is unselected
		 * @param event
		 */
		const onRowUnselect = (event: { originalEvent: Event; data: UiTableRow; index: number; type: string }) => {
			if (event.type === "checkbox") {
				if (table && table.rows) {
					table.rows[(event.data.pos -1)].selected = false;
					emit("onChangeTable", table, true);
				}
			}
		}

		/** Triggered, when all rows are selected */
		const onRowSelectAll = () => {
			if (table && table.rows) {
				table.rows.forEach(row => {
					row.selected = true;
				})
				emit("onChangeTable", table, true);
			}
		}

		/** Triggered, when all rows are unselected */
		const onRowUnselectAll = () => {
			if (table && table.rows) {
				table.rows.forEach(row => {
					row.selected = false;
				})
				emit("onChangeTable", table, true);
			}
		}

		watch(() => props.resetSelectedRows, () => {
			if (props.resetSelectedRows) {
				// reset all selected rows
				selectedRows.value = [];
			}
		})

		/**
		 * Check if columns have changed (the properties hidden, mandatory, readonly, forceValidation)
		 * If true, reload the columns
		 * @param newTable
		 * @param oldTable
		 */
		const checkColumnChanges = (newTable: DocumentTable, oldTable: DocumentTable) => {
			let columnsAreChanged: boolean = false;
			if (newTable.columns && oldTable.columns) {
				newTable.columns.forEach((column) => {
					// All logic in this function is based on the old tables column.
					// If there is no such column, return early.
					const oldColumn = oldTable.columns?.find(col => col.name === column.name);
					if (oldColumn == null) {
						return;
					}

					// set cell state to empty, when the column is force approval
					// This is necessary to show the style on the cell
					if (column.forceValidation !== oldColumn.forceValidation) {
						table.rows?.find(row => {
							row.cells?.find(cell => {
								if (cell.columnName === column.name) {
									cell.state = "";
								}
							})
						});
						columnsAreChanged = true;
						emit("onChange");
					}

					if (column.hidden !== oldColumn.hidden
						|| column.mandatory !== oldColumn.mandatory
						|| column.readonly !== oldColumn.readonly)
					{
						columnsAreChanged = true;
						return;
					}
				})
			}
			return columnsAreChanged;
		}

		watch(() => props.currentTable, (newTable: DocumentTable, oldTable: DocumentTable) => {
			Object.assign(table, props.currentTable);

			// Check if columns have changed
			const columnsAreChanged = checkColumnChanges(newTable, oldTable);

			// Only if the table has changed, the columns should be reloaded on change of the table.
			// Otherwise only reload the data
			if (newTable.name === oldTable.name && !columnsAreChanged) {
				reloadData(false);
			}
			else {
				reloadData(true);
			}

			// Re-focus a just-inserted row after the (re-)validation rebuilt the table.
			if (pendingScrollToBottom) {
				pendingScrollToBottom = false;
				void scrollToTableBottom();
			}
		})

		/**
		 * Triggers the check of values on autocomplete-fields. If the value is invalid, the value will be cleared.
		 * This function is needed for values that are inserted to fields via Squeeze-Viewer
		 * @param event
		 * @param column
		 * @param cell
		 * @param fieldValue
		 * @param tableIndex
		 */
		const checkAutocompleteValues = async (event: AutoCompleteOnCompleteEvent, column: DocumentTableColumn, cell: any, fieldValue: any = '', tableIndex: number) => {
			if (!fieldValue) {
				return;
			}

			const autocompleteRow = editingRows.value.find(row => row.pos === tableIndex);
			if (autocompleteRow) {
				await searchAutocomplete(event, column, autocompleteRow);

				if (filteredValues.value[column.name as any]) {
					if(!filteredValues.value[column.name as any].find((filteredValue: any) => filteredValue.value === fieldValue)) {
						cell.value = "";
						if (autocompleteRow.cells[column.name as any]) {
							autocompleteRow.cells[column.name as any].value = "";
						}
					} else {
						cell.value = fieldValue;
						// Set field value again, because otherwise the value might be empty
						autocompleteRow.cells[column.name as any].value = fieldValue;
					}
				}
			}
		}

		return {
			getTranslatedDescription,
			t,
			toast,
			store,
			loading,
			showTable,
			table,
			rows,
			columns,
			editingRows,
			filteredValues,
			defaultValues,
			tableCells,
			validationTableVirtualScrollerOptions,
			nextTableCell,
			noFocusColumns,
			showButton,
			selectedRows,
			allTableColumnWidths,
			isColumnWidthChanged,
			allVisibleColumns,
			columnResize,
			reloadData,
			onItemSelect,
			searchAutocomplete,
			createNewRow,
			copyRow,
			resetColumnWidths,
			scrollToTableBottom,
			createEmptyRowModelObject,
			deleteSelectedRows,
			deleteRow,
			createDefaultValues,
			onFocusField,
			findCell,
			navigateCells,
			activateCell,
			scrollToTableCell,
			isTableRowInViewport,
			determineVisibleTableRows,
			onFieldChange,
			setCellReference,
			onClickAutocomplete,
			onClickDropdown,
			onRowReorder,
			executeCustomTableAction,
			onKeydown,
			onRowSelect,
			onRowUnselect,
			onRowSelectAll,
			onRowUnselectAll,
			checkAutocompleteValues,
		}
	},
});
</script>

<style lang="scss" scoped>

.column-width-changed ::v-deep(.p-datatable-wrapper table) {
	table-layout: fixed;
}

.readonly {
	color: #2b2b2b;
	opacity: 0.5;
}

// Input fields inside table cell
.input-table {
	width: 100%;
	border-radius: 0;
	border: none;
	padding-left: 1rem;
	background: transparent;

	// Input field focussed
	&:enabled:focus {
		box-shadow: inset 0 0 0  0.1rem var(--dex-primary-color);
	}
}

// Table cell by autocomplete
::v-deep(input) {
	border: none!important;
	width: 100%;
	border-radius: 0;
	background: transparent;

	&:enabled:focus {
		box-shadow: inset 0 0 0  0.1rem var(--dex-primary-color);
	}
}

.input-autocomplete {
	padding-left: 0;
}

// Table Header
::v-deep(th, .table-header-amount) {
	background-color: #e3ebee !important;
	border-bottom-color: #e4e4e4 !important;
}

// Table cell
::v-deep(.p-cell-editing) {
	padding: 0!important;
}

// Table cell title
::v-deep(.p-column-title) {
	padding-left: 0.5rem;
	padding-right: 0.5rem;
	color: black;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Table cell editable
::v-deep(.p-editable-column.p-cell-editing) {
	padding: 0!important;
}

// Table first cell editable
::v-deep(.p-editable-column.p-cell-editing:first-child) {
	max-width: 4rem!important;
	text-align: center;
}

// Table last cell editable
::v-deep(.p-editable-column.p-cell-editing:last-child) {
	max-width: 8rem!important;
}

// Span inside table cell of column "id"
::v-deep(.column-id) {
	padding-left: 1rem!important;
}

// Table cell state error
.table-cell-border-by-error {
	box-shadow: inset 0 0 0  0.1rem var(--error-color);
}

// Table cell state force
.table-cell-border-by-force {
	box-shadow: inset 0 0 0  0.1rem var(--validationfield-forceapproval-color);
}

// Table cell state ok
.table-cell-border-by-ok {
	border: none;
}

// Hover over each row
/*
::v-deep(tr.p-selectable-row:hover) {
	background-color: var(--dex-primary-color)!important;
	color: var(--dex-text-color);
}
*/

::v-deep(button.p-button.speed-dials) {
	height: 3rem;
	width: 3rem;
}

.table-cell-amount {
	text-align: right;
}

::v-deep(th.table-header-amount) {
	justify-content: flex-end;
}

::v-deep(.p-datatable-footer) {
	/* fixed speed dial to not be hidden by viewer */
	position: fixed;
	bottom: 0.5rem;
	right: 1rem;
	background: transparent!important;
	border-width: 0!important;
}

::v-deep(.p-datatable-thead tr th .p-column-header-content) {
	overflow: hidden;
}

::v-deep(.p-autocomplete .p-autocomplete-dropdown) {
	background: #4894ac4d;
}

::v-deep(.not-resizable-column span.p-column-resizer) {
	cursor: auto;
}

::v-deep(.p-autocomplete .p-autocomplete-dropdown span.p-button-icon) {
	color: var(--primary-color);
}

// speed dial
@keyframes pop-in {
	0% {
		transform: scale(0);
	}
	100% {
		transform: scale(1);
	}
}
@keyframes pop-out {
	0% {
		transform: scale(1);
	}
	100% {
		transform: scale(0);
	}
}

.speed-dial {
	text-align: center;
}

.dials {
	margin: 0;
	padding: 0;
	list-style: none;

	li {
		margin-bottom: 0.25rem;
	}

	li.pop-in {
		animation: pop-in .3s ease both;
	}

	li.pop-out {
		animation: pop-out .3s ease both;
	}
}

::v-deep(.p-datatable-footer) {
	z-index: 1000;
	margin-right: 2rem;
}

.validation-field {
	padding: 0.5629rem 0.5629rem !important;
}

::v-deep(input.p-autocomplete-input.p-inputtext.p-component.validation-field.p-autocomplete-dd-input) {
	padding: 0.5629rem 0.5629rem !important;
}

::v-deep(button.p-button.p-component.p-button-icon-only.p-autocomplete-dropdown) {
	width: 2.5rem;
	padding: 0.5629rem 0.5629rem !important;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
